import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { saveCellTypingPanelSettings } from 'api/study';
import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import {
  cellRuleRevisionSnapshots,
  panelCellRuleMarkerIds,
} from 'components/Pages/OperationsSettings/CellRulesDataGrid/cellRuleRevisions';
import {
  cloneDeep,
  compact,
  difference,
  filter,
  find,
  flatMap,
  fromPairs,
  includes,
  isEmpty,
  isEqual,
  map,
  some,
  sortBy,
  xor,
} from 'es-toolkit/compat';
import { CalculateFeaturesParams, JobType } from 'interfaces/job';
import { MultiplexThresholdConfig } from 'interfaces/jobs/multiplex/thresholdParams';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { Permission } from 'interfaces/permissionOption';
import React, { ReactNode, useCallback, useId, useMemo, useRef, useState } from 'react';
import { cellRuleRevisionErrorMessage } from 'utils/queryHooks/cellRule/useCellRuleMutations';
import { useCellTypeOptions } from 'utils/queryHooks/useCellTypeOptions';
import { usePermissions } from 'utils/usePermissions';
import useStudy from 'utils/useStudy';
import { LoadFromPreviousJobStep } from '../../LoadFromPreviousJobStep';
import {
  getDefaultSpotDetectionSetup,
  getRnaCountMode,
  resolveSpotDetectionSetup,
} from '../../PostProcessingAndCalculateFeatures/spotDetectionInputSetup';
import {
  baselineCellRuleName,
  baselinePanelConfiguration,
  panelSettingsDiffer,
  savedCellTypingPanelSettings,
} from './cellTypingPanelSettings';
import { scopePanelConfiguration } from './executionUnits';
import {
  createPanelConfigurationDraft,
  PanelConfigurationDraft,
  PanelConfigurationEditor,
  synchronizePanelProbeThresholds,
} from './PanelConfigurationEditor';
import { PanelExecutionUnits } from './PanelExecutionUnits';
import { PanelInputJobs } from './PanelInputJobs';
import { restoreCellTypingExecution, RestoredCellTypingExecution } from './previousExecution';
import { RnaActionReview } from './RnaActionReview';

export interface PanelConfigurationSource {
  id: string;
  label: string;
  kind: 'saved' | 'execution' | 'platform';
  panelDrafts: Record<string, PanelConfigurationDraft>;
  requestedOutputsByPanel?: Record<string, CellTypingRequestedOutputs>;
}

export interface CellTypingRequestedOutputs {
  positivity: boolean;
  cellTypes: boolean;
  hScores: boolean;
}

export interface CellTypingRunSettings {
  requestedOutputs: CellTypingRequestedOutputs;
  sourceJobId?: string;
  frozenParams?: CalculateFeaturesParams;
  reusePreviousInputs: boolean;
  missingPreviousInputCount: number;
  knnPolicy: 'not_needed' | 'refit_required' | 'reuse_candidate';
}

export const defaultCellTypingRequestedOutputs: CellTypingRequestedOutputs = {
  positivity: true,
  cellTypes: true,
  hScores: true,
};

interface Props {
  panels: MultiplexPanelView[];
  selectedSlideIds: string[];
  configurationSources: PanelConfigurationSource[];
  onClose: () => void;
  isLoading?: boolean;
  error?: string;
  studyId?: string;
  labId?: string;
  thresholdFlowDefaults?: MultiplexThresholdConfig;
  renderManualActions?: (
    panelDrafts: Record<string, PanelConfigurationDraft>,
    slideIds: string[],
    settings: CellTypingRunSettings
  ) => ReactNode;
}

export const CellTypingMarkerPositivityDialog = ({
  panels,
  selectedSlideIds: initialSelectedSlideIds,
  configurationSources,
  onClose,
  isLoading = false,
  error,
  renderManualActions,
  studyId,
  labId,
  thresholdFlowDefaults,
}: Props) => {
  const titleId = useId();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { study, isInitialLoading: isLoadingStudy } = useStudy(studyId, { enabled: Boolean(studyId) });
  const rnaCountMode = getRnaCountMode(study?.platformSettings?.dockerImage);
  const { data: cellTypes, isLoading: isLoadingCellTypes, isError: isCellTypesError } = useCellTypeOptions();
  const [previousExecution, setPreviousExecution] = useState<RestoredCellTypingExecution | null>(null);
  const [isPreviousPickerOpen, setIsPreviousPickerOpen] = useState(false);
  const [isPreviousLoading, setIsPreviousLoading] = useState(false);
  const [previousError, setPreviousError] = useState('');
  const [reusePreviousInputs, setReusePreviousInputs] = useState(true);
  const [pickerReset, setPickerReset] = useState(0);
  const [settingsNotice, setSettingsNotice] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveNotice, setSaveNotice] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [cellRuleNames, setCellRuleNames] = useState<Record<string, string>>({});
  const [persistedSettings, setPersistedSettings] = useState(study?.settings?.cellTypingPanelSettings ?? []);
  const [revisionOverrides, setRevisionOverrides] = useState(study?.settings?.multiplexPanelCellRules ?? []);
  const savedSettingsSource = useMemo((): PanelConfigurationSource | null => {
    if (!persistedSettings.length) return null;
    return {
      id: 'saved-panel-settings',
      kind: 'saved',
      label: 'Saved panel settings',
      panelDrafts: fromPairs(
        compact(
          map(persistedSettings, (entry) => {
            const panel = find(panels, { panelId: entry.panelId });
            if (!panel) return null;
            return [
              entry.panelId,
              {
                ...createPanelConfigurationDraft(panel),
                ...(entry.configuration as Partial<PanelConfigurationDraft>),
              },
            ];
          })
        )
      ),
      requestedOutputsByPanel: fromPairs(
        compact(
          map(persistedSettings, (entry) => (entry.requestedOutputs ? [entry.panelId, entry.requestedOutputs] : null))
        )
      ),
    };
  }, [panels, persistedSettings]);
  const sources = [
    ...(savedSettingsSource ? [savedSettingsSource] : []),
    ...(previousExecution ? [...configurationSources, previousExecution] : configurationSources).filter(
      (entry) => !savedSettingsSource || entry.kind !== 'saved'
    ),
  ];
  const [sourceChoiceId, setSourceChoiceId] = useState<string | null>(null);
  const defaultSource = find(sources, { kind: 'saved' }) ?? find(sources, { kind: 'platform' });
  const sourceId = sourceChoiceId ?? defaultSource?.id ?? '';
  const [mode, setMode] = useState<'manual' | 'coordinated'>(renderManualActions ? 'manual' : 'coordinated');
  const [selectedSlideIds, setSelectedSlideIds] = useState(initialSelectedSlideIds);
  const [drafts, setDrafts] = useState<Record<string, PanelConfigurationDraft>>({});
  const [resetVersion, setResetVersion] = useState(0);
  const [expandedPanelId, setExpandedPanelId] = useState<string | null>(null);
  const [executionDirty, setExecutionDirty] = useState(false);
  const source = find(sources, { id: sourceId });
  const [requestedOutputsByPanel, setRequestedOutputsByPanel] = useState<Record<string, CellTypingRequestedOutputs>>(
    {}
  );
  const currentRequestedOutputs = fromPairs(
    map(panels, (panel) => [
      panel.panelId,
      requestedOutputsByPanel[panel.panelId] ??
        source?.requestedOutputsByPanel?.[panel.panelId] ??
        defaultCellTypingRequestedOutputs,
    ])
  ) as Record<string, CellTypingRequestedOutputs>;
  const previousInputSlideIds = previousExecution
    ? Object.values(previousExecution.frozenParams.inputs).flatMap((caseInputs) => Object.keys(caseInputs))
    : [];
  const missingPreviousInputCount =
    source?.kind === 'execution' ? difference(selectedSlideIds, previousInputSlideIds).length : 0;
  const isDirty =
    executionDirty ||
    (source?.kind === 'execution' && !reusePreviousInputs) ||
    some(
      panels,
      (panel) =>
        !isEqual(
          currentRequestedOutputs[panel.panelId],
          source?.requestedOutputsByPanel?.[panel.panelId] ?? defaultCellTypingRequestedOutputs
        )
    ) ||
    !isEmpty(xor(selectedSlideIds, initialSelectedSlideIds)) ||
    some(
      panels,
      (panel) =>
        drafts[panel.panelId] &&
        !isEqual(
          drafts[panel.panelId],
          synchronizePanelProbeThresholds(
            panel,
            source?.panelDrafts[panel.panelId] ?? createPanelConfigurationDraft(panel)
          )
        )
    );
  const unknownSelectionCount = difference(
    selectedSlideIds,
    flatMap(panels, (panel) => map(panel.slides, 'slideId'))
  ).length;
  const settingsEditedRef = useRef(false);
  const resetDrafts = () => {
    setDrafts({});
    setCellRuleNames({});
    setExecutionDirty(false);
    setSelectedSlideIds(initialSelectedSlideIds);
    setResetVersion((current) => current + 1);
    setRequestedOutputsByPanel({});
    setReusePreviousInputs(true);
  };
  const blockUnsavedSettingsChange = () => {
    if (!settingsEditedRef.current) return false;
    setSettingsNotice('Save settings before changing the starting configuration.');
    return true;
  };
  const changeSource = (nextSourceId: string) => {
    if (nextSourceId === sourceId || blockUnsavedSettingsChange()) return;
    setSettingsNotice('');
    setSourceChoiceId(nextSourceId);
    resetDrafts();
    setPreviousError('');
  };
  const currentPanelDrafts: Record<string, PanelConfigurationDraft> = fromPairs(
    map(panels, (panel) => [
      panel.panelId,
      synchronizePanelProbeThresholds(panel, {
        ...(drafts[panel.panelId] ?? source?.panelDrafts[panel.panelId] ?? createPanelConfigurationDraft(panel)),
        spotDetectionSetup: resolveSpotDetectionSetup(
          (drafts[panel.panelId] ?? source?.panelDrafts[panel.panelId])?.spotDetectionSetup ??
            getDefaultSpotDetectionSetup(),
          rnaCountMode
        ),
      }),
    ])
  );
  const mergedRevisions = useMemo(() => {
    const stored = study?.settings?.multiplexPanelCellRules ?? [];
    return [
      ...stored.map((entry) => {
        const override = revisionOverrides.find(
          (item) => (item.panelId ?? null) === (entry.panelId ?? null) && item.revision === entry.revision
        );
        return override ?? entry;
      }),
      ...revisionOverrides.filter(
        (entry) =>
          !stored.some(
            (storedEntry) =>
              (storedEntry.panelId ?? null) === (entry.panelId ?? null) && storedEntry.revision === entry.revision
          )
      ),
    ];
  }, [revisionOverrides, study?.settings?.multiplexPanelCellRules]);
  const canSaveSettings =
    Boolean(studyId) &&
    (hasPermission(Permission.EditQCMarkers) ||
      hasPermission(Permission.EditCellRules) ||
      hasPermission(Permission.EditStudyNameAndDescription));
  const unsavedPanelIds = panels.flatMap((panel) => {
    const saved = savedCellTypingPanelSettings(persistedSettings, panel.panelId);
    const baselineOutputs =
      saved?.requestedOutputs ?? source?.requestedOutputsByPanel?.[panel.panelId] ?? defaultCellTypingRequestedOutputs;
    const baselineName = baselineCellRuleName(panel.panelId, mergedRevisions, saved);
    return panelSettingsDiffer({
      draft: currentPanelDrafts[panel.panelId],
      baselineDraft: baselinePanelConfiguration(panel, source?.panelDrafts[panel.panelId], mergedRevisions, saved),
      name: cellRuleNames[panel.panelId] ?? baselineName,
      baselineName,
      requestedOutputs: currentRequestedOutputs[panel.panelId],
      baselineOutputs,
    })
      ? [panel.panelId]
      : [];
  });
  const settingsUnsaved = unsavedPanelIds.length > 0;
  settingsEditedRef.current = some(panels, (panel) => {
    const saved = source?.kind === 'saved' ? savedCellTypingPanelSettings(persistedSettings, panel.panelId) : undefined;
    const baselineName = baselineCellRuleName(panel.panelId, mergedRevisions, saved);
    return panelSettingsDiffer({
      draft: currentPanelDrafts[panel.panelId],
      baselineDraft: baselinePanelConfiguration(panel, source?.panelDrafts[panel.panelId], mergedRevisions, saved),
      name: cellRuleNames[panel.panelId] ?? baselineName,
      baselineName,
      requestedOutputs: currentRequestedOutputs[panel.panelId],
      baselineOutputs: source?.requestedOutputsByPanel?.[panel.panelId] ?? defaultCellTypingRequestedOutputs,
    });
  });
  const rememberCellRuleName = useCallback((panelId: string, name: string) => {
    setCellRuleNames((current) => (current[panelId] === name ? current : { ...current, [panelId]: name }));
  }, []);
  const saveSettings = async () => {
    if (!studyId || isSavingSettings || !settingsUnsaved) return;
    setIsSavingSettings(true);
    setSaveError('');
    setSaveNotice('');
    try {
      let lastNotice = '';
      for (const panel of panels.filter((entry) => unsavedPanelIds.includes(entry.panelId))) {
        const saved = savedCellTypingPanelSettings(persistedSettings, panel.panelId);
        const draft = currentPanelDrafts[panel.panelId];
        const rules = currentRequestedOutputs[panel.panelId].cellTypes
          ? cellRuleRevisionSnapshots(draft.cellTypeIds, draft.cellRules, panelCellRuleMarkerIds(panel))
          : [];
        const name = (
          cellRuleNames[panel.panelId] ?? baselineCellRuleName(panel.panelId, mergedRevisions, saved)
        ).trim();
        const response = await saveCellTypingPanelSettings({
          studyId,
          panelId: panel.panelId,
          name: name || null,
          rules,
          configuration: draft,
          requestedOutputs: currentRequestedOutputs[panel.panelId],
        });
        setPersistedSettings((current) => [...current.filter((entry) => entry.panelId !== panel.panelId), response]);
        if (response.revision) {
          setRevisionOverrides((current) => [
            ...current.filter(
              (entry) =>
                (entry.panelId ?? null) !== (response.revision?.panelId ?? null) ||
                entry.revision !== response.revision.revision
            ),
            response.revision,
          ]);
        }
        lastNotice = response.reusedExistingRevision
          ? `Saved settings. Using ${response.cellRuleName || `revision ${response.cellRuleRevision}`}.`
          : response.cellRuleRevision
          ? `Saved settings. Cell rules revision ${response.cellRuleRevision} is ready.`
          : 'Saved settings for this panel.';
      }
      setSaveNotice(lastNotice);
      setSettingsNotice('');
      void queryClient.invalidateQueries(['study', studyId]);
    } catch (saveFailure) {
      setSaveError(cellRuleRevisionErrorMessage(saveFailure as { message?: string }));
    } finally {
      setIsSavingSettings(false);
    }
  };
  const panelHasMifRules = (panel: MultiplexPanelView) =>
    currentRequestedOutputs[panel.panelId].cellTypes &&
    some(
      currentPanelDrafts[panel.panelId].cellRules,
      (rule) =>
        rule.ruleValue !== -1 &&
        some(
          panel.channels,
          (channel) => channel.saved?.channelModality === 'mif' && channel.saved.analysisName === rule.stainTypeId
        )
    );
  const needsKnnRefit = (panel: MultiplexPanelView) => {
    const current = scopePanelConfiguration(panel, currentPanelDrafts[panel.panelId]);
    const previousDraft = previousExecution?.panelDrafts[panel.panelId];
    const previous = previousDraft ? scopePanelConfiguration(panel, previousDraft) : undefined;
    const mifIds = map(
      filter(panel.channels, (channel) => channel.saved?.channelModality === 'mif'),
      (channel) => channel.saved!.analysisName
    );
    const getMifRules = (draft: PanelConfigurationDraft) =>
      sortBy(
        filter(draft.cellRules, (rule) => includes(mifIds, rule.stainTypeId) && rule.ruleValue !== -1),
        ['cellTypeId', 'stainTypeId']
      );
    return (
      !previous ||
      !isEqual(getMifRules(current), getMifRules(previous)) ||
      !isEqual(current.markerThresholds, previous.markerThresholds) ||
      current.knnTrainingScope !== previous.knnTrainingScope
    );
  };
  const getKnnPolicy = (panel: MultiplexPanelView): CellTypingRunSettings['knnPolicy'] =>
    !panelHasMifRules(panel)
      ? 'not_needed'
      : source?.kind !== 'execution' ||
        !reusePreviousInputs ||
        needsKnnRefit(panel) ||
        !previousExecution?.frozenParams.pipsStudyInputs?.knnClassifier
      ? 'refit_required'
      : 'reuse_candidate';

  const loadPreviousExecution = async (job: Parameters<typeof restoreCellTypingExecution>[0] | undefined) => {
    if (blockUnsavedSettingsChange()) {
      setPickerReset((current) => current + 1);
      return;
    }
    if (!job) {
      setPreviousExecution(null);
      setSourceChoiceId(null);
      resetDrafts();
      return;
    }
    try {
      const selectedPanels = filter(panels, (panel) =>
        some(panel.slides, (slide) => includes(selectedSlideIds, slide.slideId))
      );
      const scopedPanels = map(selectedPanels, (panel) => ({
        ...panel,
        slides: filter(panel.slides, (slide) => includes(selectedSlideIds, slide.slideId)),
      }));
      const restored = restoreCellTypingExecution(job, studyId || '', scopedPanels, cellTypes || []);
      setPreviousExecution(restored);
      setExecutionDirty(false);
      setSourceChoiceId(restored.id);
      setDrafts({});
      setResetVersion((current) => current + 1);
      setRequestedOutputsByPanel({});
      setReusePreviousInputs(true);
      setPreviousError('');
      setIsPreviousLoading(false);
      setIsPreviousPickerOpen(false);
    } catch (restoreError) {
      setPreviousError(
        restoreError instanceof Error ? restoreError.message : 'Previous execution could not be restored.'
      );
      setIsPreviousLoading(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="xl" aria-labelledby={titleId} onClose={onClose}>
      <DialogChromeHeader titleId={titleId} title="Cell typing & marker positivity" onClose={onClose} />
      <DialogContent dividers sx={{ pt: 0, scrollPaddingTop: 64 }}>
        <Stack spacing={2} sx={{ pt: 2.5 }}>
          {isLoading || (Boolean(studyId) && isLoadingStudy) ? (
            <Skeleton height={320} variant="rectangular" aria-label="Loading panel configuration" />
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : isEmpty(panels) ? (
            <Alert severity="info">No multiplex panels in the selected scope.</Alert>
          ) : (
            <>
              <TextField
                select
                size="small"
                label="Starting configuration"
                sx={{ maxWidth: 360 }}
                value={sourceId}
                disabled={isEmpty(configurationSources)}
                onChange={(event) => void changeSource(event.target.value)}
              >
                {map(sources, (configuration) => (
                  <MenuItem key={configuration.id} value={configuration.id}>
                    {configuration.label}
                  </MenuItem>
                ))}
              </TextField>
              {settingsNotice ? <Alert severity="info">{settingsNotice}</Alert> : null}
              {saveError ? <Alert severity="error">{saveError}</Alert> : null}
              {saveNotice ? <Alert severity="success">{saveNotice}</Alert> : null}
              {studyId && labId && hasPermission(Permission.ViewJobs) && (
                <>
                  <Button
                    color="secondary"
                    variant="outlined"
                    onClick={() => setIsPreviousPickerOpen((current) => !current)}
                  >
                    Reuse previous run
                  </Button>
                  {isPreviousPickerOpen &&
                    (isLoadingCellTypes ? (
                      <Skeleton height={80} aria-label="Loading cell types for restore" />
                    ) : (
                      <LoadFromPreviousJobStep
                        key={pickerReset}
                        jobType={JobType.PostProcessingAndCalculateFeatures}
                        queryScope={{ studyId, labId }}
                        selectedJobId={source?.kind === 'execution' ? previousExecution?.sourceJobId || '' : ''}
                        onSelectedJob={(job) => void loadPreviousExecution(job)}
                        onSelectedJobLoadingChange={setIsPreviousLoading}
                      />
                    ))}
                  {previousError && <Alert severity="error">{previousError}</Alert>}
                </>
              )}
              {!source && <Alert severity="info">Select an available starting configuration.</Alert>}
              <Typography variant="body2">
                {selectedSlideIds.length} selected slides across {panels.length} panels
              </Typography>
              {unknownSelectionCount > 0 && (
                <Alert severity="warning">
                  {unknownSelectionCount} selected slides are outside the current panel scope.
                </Alert>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Stack component="aside" spacing={2} alignItems="flex-start" aria-label="This run">
                  <Typography variant="h3">This run</Typography>
                  <Typography variant="body2">{selectedSlideIds.length} selected slides</Typography>
                  {source?.kind === 'execution' && (
                    <>
                      <Typography variant="subtitle2">Previous-run inputs</Typography>
                      <FormControlLabel
                        label="Reuse stored input references"
                        control={
                          <Checkbox
                            checked={reusePreviousInputs}
                            onChange={(_event, checked) => setReusePreviousInputs(checked)}
                          />
                        }
                      />
                      <Typography variant="caption">{previousExecution?.label}</Typography>
                      {missingPreviousInputCount > 0 && (
                        <Alert severity="warning">
                          {missingPreviousInputCount} selected slides have no stored inputs in this run.
                        </Alert>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Exact artifact versions and availability require backend validation.
                      </Typography>
                      {!reusePreviousInputs && (
                        <Alert severity="info">Select replacement inputs before executing.</Alert>
                      )}
                    </>
                  )}
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={mode}
                    aria-label="Execution mode"
                    onChange={(_event, nextMode: 'manual' | 'coordinated' | null) => {
                      if (nextMode) setMode(nextMode);
                    }}
                  >
                    <ToggleButton value="manual">Manual</ToggleButton>
                    <ToggleButton value="coordinated">Coordinated</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <PanelExecutionUnits
                  key={`${sourceId}-${resetVersion}`}
                  studyId={studyId}
                  labId={labId}
                  panels={panels}
                  drafts={currentPanelDrafts}
                  selectedSlideIds={selectedSlideIds}
                  requestedOutputsByPanel={currentRequestedOutputs}
                  cellTypes={cellTypes}
                  isLoadingCellTypes={isLoadingCellTypes}
                  isCellTypesError={isCellTypesError}
                  onExecutionDirtyChange={setExecutionDirty}
                  onCellTypesChange={(panelId, enabled) =>
                    setRequestedOutputsByPanel((current) => ({
                      ...current,
                      [panelId]: { ...currentRequestedOutputs[panelId], cellTypes: enabled },
                    }))
                  }
                  renderPanel={(panel, runAction, executionSettings, activePanel, activeUnits) => {
                    const requestedOutputs = currentRequestedOutputs[panel.panelId];
                    const activeDraft = scopePanelConfiguration(activePanel, currentPanelDrafts[panel.panelId]);
                    const panelSlideIds = filter(selectedSlideIds, (slideId) => some(panel.slides, { slideId }));
                    const panelMissingInputCount =
                      source?.kind === 'execution' ? difference(panelSlideIds, previousInputSlideIds).length : 0;
                    const knnPolicy = getKnnPolicy(activePanel);
                    return (
                      <PanelConfigurationEditor
                        key={`${panel.panelId}-${sourceId}-${resetVersion}`}
                        panel={activePanel}
                        expanded={expandedPanelId === panel.panelId}
                        onExpandedChange={(nextExpanded) => setExpandedPanelId(nextExpanded ? panel.panelId : null)}
                        requestedOutputs={requestedOutputs}
                        activeUnits={mode === 'coordinated' ? activeUnits : undefined}
                        thresholdFlowDefaults={thresholdFlowDefaults}
                        selectedSlideIds={panelSlideIds}
                        cellRuleRevisions={mergedRevisions}
                        initialCellRuleRevision={
                          source?.kind === 'saved'
                            ? mergedRevisions.find(
                                (revision) =>
                                  revision.panelId === panel.panelId &&
                                  revision.revision ===
                                    savedCellTypingPanelSettings(persistedSettings, panel.panelId)?.cellRuleRevision
                              )
                            : undefined
                        }
                        onCellRuleNameChange={(name) => rememberCellRuleName(panel.panelId, name)}
                        initialDraft={cloneDeep(currentPanelDrafts[panel.panelId])}
                        runAction={mode === 'coordinated' ? runAction : undefined}
                        executionSettings={
                          <Stack spacing={2} divider={<Divider />}>
                            <Stack role="group" aria-label="Requested outputs" spacing={1}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <ChecklistOutlinedIcon color="action" />
                                <Typography variant="h3" component="h3">
                                  Requested outputs
                                </Typography>
                              </Stack>
                              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                {map(
                                  [
                                    { key: 'positivity' as const, label: 'Marker & probe positivity' },
                                    { key: 'cellTypes' as const, label: 'Cell types' },
                                    { key: 'hScores' as const, label: 'H-scores' },
                                  ],
                                  (output) => (
                                    <FormControlLabel
                                      key={output.key}
                                      label={output.label}
                                      control={
                                        <Checkbox
                                          checked={requestedOutputs[output.key]}
                                          onChange={(_event, checked) =>
                                            setRequestedOutputsByPanel((current) => ({
                                              ...current,
                                              [panel.panelId]: {
                                                ...(current[panel.panelId] ?? requestedOutputs),
                                                [output.key]: checked,
                                              },
                                            }))
                                          }
                                        />
                                      }
                                    />
                                  )
                                )}
                              </Stack>
                              {!some(requestedOutputs, Boolean) && (
                                <Alert severity="info">Select at least one output.</Alert>
                              )}
                            </Stack>
                            {knnPolicy !== 'not_needed' && (
                              <Alert severity="info">
                                {knnPolicy === 'refit_required'
                                  ? 'KNN fitting is required for the current mIF rules or cutoffs.'
                                  : 'The previous KNN model is a reuse candidate; compatibility still needs validation.'}
                              </Alert>
                            )}
                            {mode === 'coordinated'
                              ? executionSettings
                              : some(requestedOutputs, Boolean) && !isPreviousLoading && panelMissingInputCount === 0
                              ? renderManualActions?.({ [panel.panelId]: activeDraft }, panelSlideIds, {
                                  requestedOutputs,
                                  sourceJobId:
                                    source?.kind === 'execution' ? previousExecution?.sourceJobId : undefined,
                                  frozenParams:
                                    source?.kind === 'execution'
                                      ? cloneDeep(previousExecution?.frozenParams)
                                      : undefined,
                                  reusePreviousInputs:
                                    source?.kind === 'execution' && reusePreviousInputs && !isPreviousLoading,
                                  missingPreviousInputCount: panelMissingInputCount,
                                  knnPolicy,
                                }) ?? (
                                  <Typography variant="body2" color="text.secondary">
                                    Manual runs reuse inputs from the selected previous run.
                                  </Typography>
                                )
                              : null}
                            {(mode === 'manual' || activeUnits.includes('calculate_features')) &&
                              filter(activePanel.channels, (channel) => channel.saved?.channelModality === 'rna')
                                .length ===
                                filter(panel.channels, (channel) => channel.saved?.channelModality === 'rna')
                                  .length && (
                                <RnaActionReview
                                  studyId={studyId}
                                  labId={labId}
                                  panels={[activePanel]}
                                  drafts={{ [panel.panelId]: activeDraft }}
                                  selectedSlideIds={panelSlideIds}
                                  requestedOutputs={requestedOutputs}
                                  manualSource={
                                    source?.kind === 'execution' && reusePreviousInputs && !isPreviousLoading
                                      ? previousExecution ?? undefined
                                      : undefined
                                  }
                                  allowManual={mode === 'manual'}
                                />
                              )}
                            <PanelInputJobs
                              studyId={studyId}
                              labId={labId}
                              panel={panel}
                              selectedSlideIds={panelSlideIds}
                              manualSource={
                                mode === 'manual' &&
                                source?.kind === 'execution' &&
                                reusePreviousInputs &&
                                !isPreviousLoading
                                  ? previousExecution ?? undefined
                                  : undefined
                              }
                            />
                          </Stack>
                        }
                        onSelectionChange={(slideId, selected) =>
                          setSelectedSlideIds((current) =>
                            selected
                              ? includes(current, slideId)
                                ? current
                                : [...current, slideId]
                              : filter(current, (currentId) => currentId !== slideId)
                          )
                        }
                        onDraftChange={(draft) => setDrafts((current) => ({ ...current, [panel.panelId]: draft }))}
                      />
                    );
                  }}
                />
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogChromeFooter>
        {canSaveSettings && settingsUnsaved && (
          <Button
            variant="contained"
            color="secondary"
            disabled={isSavingSettings || !studyId}
            startIcon={isSavingSettings ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => void saveSettings()}
          >
            {isSavingSettings ? 'Saving settings' : 'Save settings'}
          </Button>
        )}
        <Button color="inherit" onClick={onClose}>
          Close
        </Button>
      </DialogChromeFooter>
    </Dialog>
  );
};
